import React, { useMemo } from 'react';
import { Deal, TeamMember, Stage } from '../types/pipeline';
import { HeatMapCell } from './HeatMapCell';
import { HeatMapLegend } from './HeatMapLegend';
import { STAGES } from '../data/mockPipelineData';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
interface HeatMapGridProps {
  members: TeamMember[];
  deals: Deal[];
  metric: 'count' | 'value';
  onMetricChange: (metric: 'count' | 'value') => void;
  onCellClick: (memberId: string, stage: Stage) => void;
}
export function HeatMapGrid({
  members,
  deals,
  metric,
  onMetricChange,
  onCellClick
}: HeatMapGridProps) {
  // Calculate max value for color scaling
  const maxValue = useMemo(() => {
    let max = 0;
    members.forEach(member => {
      STAGES.forEach(stage => {
        const stageDeals = deals.filter(d => d.assignedTo === member.id && d.stage === stage);
        const value = metric === 'count' ? stageDeals.length : stageDeals.reduce((sum, d) => sum + d.amount, 0);
        if (value > max) max = value;
      });
    });
    return max;
  }, [members, deals, metric]);
  // Get deals for a specific member and stage
  const getDealsForCell = (memberId: string, stage: Stage) => {
    return deals.filter(d => d.assignedTo === memberId && d.stage === stage);
  };
  // Calculate member totals
  const getMemberTotal = (memberId: string) => {
    const memberDeals = deals.filter(d => d.assignedTo === memberId);
    if (metric === 'count') return memberDeals.length;
    return memberDeals.reduce((sum, d) => sum + d.amount, 0);
  };
  // Calculate stage totals
  const getStageTotal = (stage: Stage) => {
    const stageDeals = deals.filter(d => d.stage === stage);
    if (metric === 'count') return stageDeals.length;
    return stageDeals.reduce((sum, d) => sum + d.amount, 0);
  };
  // Mock sparkline data (in real app, would come from historical data)
  const generateSparklineData = () => {
    return Array.from({
      length: 30
    }, () => Math.random() * 10 + 5);
  };
  const formatTotal = (value: number) => {
    if (metric === 'value') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(value);
    }
    return value.toString();
  };
  const getStageColor = (index: number) => {
    const colors = ['text-blue-600', 'text-indigo-600', 'text-violet-600', 'text-fuchsia-600', 'text-emerald-600'];
    return colors[index] || 'text-slate-600';
  };
  return <div className="space-y-6" data-id="element-30">
      {/* Controls */}
      <div className="flex items-center justify-between" data-id="element-31">
        <div data-id="element-32">
          <h3 className="text-lg font-semibold text-slate-900" data-id="element-33">
            Pipeline Heat Map
          </h3>
          <p className="text-sm text-slate-500" data-id="element-34">
            Visualize deal distribution and identify bottlenecks
          </p>
        </div>

        <div className="flex items-center space-x-2 bg-white border border-slate-200 rounded-lg p-1" data-id="element-35">
          <button onClick={() => onMetricChange('count')} className={`px-4 py-2 rounded text-sm font-medium transition-colors ${metric === 'count' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-36">
            Deal Count
          </button>
          <button onClick={() => onMetricChange('value')} className={`px-4 py-2 rounded text-sm font-medium transition-colors ${metric === 'value' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-37">
            Pipeline Value
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-38">
        {/* Heat Map Grid */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-39">
          <div className="overflow-x-auto" data-id="element-40">
            <table className="w-full" data-id="element-41">
              <thead data-id="element-42">
                <tr className="bg-slate-50 border-b border-slate-200" data-id="element-43">
                  <th className="sticky left-0 z-10 bg-slate-50 px-6 py-4 text-left" data-id="element-44">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-45">
                      Team Member
                    </div>
                  </th>
                  {STAGES.map((stage, index) => <th key={stage} className="px-4 py-4 text-center min-w-[140px]" data-id="element-46">
                      <div className={`text-xs font-semibold uppercase tracking-wider ${getStageColor(index)}`} data-id="element-47">
                        {stage}
                      </div>
                      <div className="text-xs text-slate-500 mt-1" data-id="element-48">
                        {formatTotal(getStageTotal(stage))}
                      </div>
                    </th>)}
                  <th className="px-6 py-4 text-center" data-id="element-49">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-50">
                      Total
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-51">
                {members.map((member, memberIndex) => {
                const memberTotal = getMemberTotal(member.id);
                const sparklineData = generateSparklineData();
                const trend = sparklineData[sparklineData.length - 1] > sparklineData[0] ? 'up' : sparklineData[sparklineData.length - 1] < sparklineData[0] ? 'down' : 'flat';
                return <tr key={member.id} className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${memberIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-52">
                      <td className="sticky left-0 z-10 bg-inherit px-6 py-4" data-id="element-53">
                        <div className="flex items-center space-x-3" data-id="element-54">
                          <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" data-id="element-55" />
                          <div data-id="element-56">
                            <div className="font-semibold text-sm text-slate-900" data-id="element-57">
                              {member.name}
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-58">
                              {member.role}
                            </div>
                          </div>
                        </div>
                      </td>
                      {STAGES.map(stage => <td key={stage} className="px-4 py-4" data-id="element-59">
                          <HeatMapCell deals={getDealsForCell(member.id, stage)} metric={metric} maxValue={maxValue} teamMemberName={member.name} stage={stage} onClick={() => onCellClick(member.id, stage)} data-id="element-60" />
                        </td>)}
                      <td className="px-6 py-4" data-id="element-61">
                        <div className="text-center" data-id="element-62">
                          <div className="text-lg font-bold text-slate-900" data-id="element-63">
                            {formatTotal(memberTotal)}
                          </div>
                          <div className="flex items-center justify-center mt-1 text-xs" data-id="element-64">
                            {trend === 'up' && <TrendingUp className="w-3 h-3 text-green-600 mr-1" data-id="element-65" />}
                            {trend === 'down' && <TrendingDown className="w-3 h-3 text-red-600 mr-1" data-id="element-66" />}
                            {trend === 'flat' && <Minus className="w-3 h-3 text-slate-400 mr-1" data-id="element-67" />}
                            <span className={trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500'} data-id="element-68">
                              30d trend
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="lg:col-span-1" data-id="element-69">
          <HeatMapLegend metric={metric} maxValue={maxValue} data-id="element-70" />
        </div>
      </div>
    </div>;
}